import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Typography
} from '@mui/material';
import { useDrag } from '@use-gesture/react';
import { motion, useMotionValue, useSpring } from 'motion/react';

const maplibre = window.maplibregl;

const SELECTED_FILL = '#2e7d32';
const SELECTED_STROKE = '#1b5e20';
const UNSELECTED_FILL = '#8B6BB5';
const UNSELECTED_STROKE = '#59458C';
const PASTEL_PURPLE = '#59458C';
const PASTEL_PURPLE_DARK = '#331B65';

function createCircle(center, radiusMeters) {
  const points = 64;
  const coords = [];
  for (let i = 0; i < points; i++) {
    const angle = (i * 360) / points;
    const dx = radiusMeters * Math.cos((angle * Math.PI) / 180);
    const dy = radiusMeters * Math.sin((angle * Math.PI) / 180);
    const latOffset = dy / 111320;
    const lngOffset = dx / (111320 * Math.cos((center[1] * Math.PI) / 180));
    coords.push([center[0] + lngOffset, center[1] + latOffset]);
  }
  coords.push(coords[0]);
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [coords] }
  };
}

function haversineMiles(lat1, lon1, lat2, lon2) {
  const R = 3958.8;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

function milesToLatDegrees(miles) {
  return miles / 69;
}

function milesToLngDegrees(miles, lat) {
  return miles / (69 * Math.max(0.2, Math.cos((lat * Math.PI) / 180)));
}

function fitUserAndTroops(map, userCenter, focusZones, bottomPadding = 80) {
  if (!map || !userCenter || !maplibre) return;
  const bounds = new maplibre.LngLatBounds(userCenter, userCenter);

  focusZones.forEach((z) => {
    if (z.centerLng == null || z.centerLat == null) return;
    const radius = Math.max(Number(z.radiusMiles) || 1, 1);
    const rLat = milesToLatDegrees(radius);
    const rLng = milesToLngDegrees(radius, z.centerLat);
    bounds.extend([z.centerLng - rLng, z.centerLat - rLat]);
    bounds.extend([z.centerLng + rLng, z.centerLat + rLat]);
    bounds.extend([z.centerLng, z.centerLat]);
  });

  map.fitBounds(bounds, {
    padding: { top: 56, bottom: Math.max(80, bottomPadding), left: 40, right: 40 },
    maxZoom: 11,
    duration: 350
  });
}

function buildFeatures(zones, selectedZoneId) {
  return zones.map((z) => {
    const circle = createCircle([z.centerLng, z.centerLat], z.radiusMiles * 1609.34);
    circle.properties = { id: z.id, name: z.name, selected: z.id === selectedZoneId };
    return circle;
  });
}

function markerStyle(el, selected) {
  el.style.background = selected ? SELECTED_FILL : UNSELECTED_FILL;
  el.style.color = 'white';
  el.style.padding = '4px 10px';
  el.style.borderRadius = '14px';
  el.style.fontSize = '12px';
  el.style.fontWeight = '700';
  el.style.cursor = 'pointer';
  el.style.whiteSpace = 'nowrap';
  el.style.boxShadow = selected
    ? '0 2px 8px rgba(46,125,50,0.45)'
    : '0 2px 6px rgba(51,27,101,0.28)';
  el.style.border = selected ? `2px solid ${SELECTED_STROKE}` : `1px solid ${UNSELECTED_STROKE}`;
}

/**
 * Full-bleed map + draggable Closest Troops sheet.
 * userCenter: [lng, lat]
 * zones: [{ id, name, centerLng, centerLat, radiusMiles }]
 */
export default function TroopZonePickerMap({
  userCenter,
  zones = [],
  selectedZoneId,
  onSelectZone,
  onConfirm,
  onBack,
  confirmLabel = 'Join this Troop',
  busy = false,
  fillParent = true
}) {
  const rootRef = useRef(null);
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const onSelectZoneRef = useRef(onSelectZone);
  const selectedZoneIdRef = useRef(selectedZoneId);
  const sheetHeightRef = useRef(0);

  const [viewportH, setViewportH] = useState(() => window.innerHeight || 700);
  const [snap, setSnap] = useState('half'); // peek | half | expanded

  const y = useMotionValue(0);
  const springY = useSpring(y, { stiffness: 320, damping: 34 });

  onSelectZoneRef.current = onSelectZone;
  selectedZoneIdRef.current = selectedZoneId;

  const ranked = useMemo(() => (
    [...zones]
      .map((z) => ({
        ...z,
        distanceMiles: userCenter
          ? haversineMiles(userCenter[1], userCenter[0], z.centerLat, z.centerLng)
          : null
      }))
      .sort((a, b) => (a.distanceMiles ?? 99999) - (b.distanceMiles ?? 99999))
  ), [zones, userCenter?.[0], userCenter?.[1]]);

  const selectedZone = ranked.find((z) => z.id === selectedZoneId) || ranked[0] || null;

  const snaps = useMemo(() => {
    const h = viewportH;
    return {
      peek: Math.round(h * 0.32),
      half: Math.round(h * 0.52),
      expanded: Math.round(h * 0.82)
    };
  }, [viewportH]);

  const sheetHeight = snaps[snap] || snaps.half;
  sheetHeightRef.current = sheetHeight;

  // Keep sheet motion in sync with snap target (distance from bottom = sheet height)
  useEffect(() => {
    const maxH = snaps.expanded;
    y.set(maxH - sheetHeight);
  }, [snap, snaps.expanded, sheetHeight, y]);

  const bind = useDrag(
    ({ movement: [, my], down, velocity: [, vy], last }) => {
      const maxH = snaps.expanded;
      const base = maxH - (snaps[snap] || snaps.half);
      if (down) {
        y.set(Math.max(0, Math.min(maxH - snaps.peek, base + my)));
        return;
      }
      if (!last) return;

      const current = base + my;
      const candidates = [
        { key: 'peek', offset: maxH - snaps.peek },
        { key: 'half', offset: maxH - snaps.half },
        { key: 'expanded', offset: maxH - snaps.expanded }
      ];

      let next = 'half';
      if (vy > 0.45) {
        next = snap === 'expanded' ? 'half' : 'peek';
      } else if (vy < -0.45) {
        next = snap === 'peek' ? 'half' : 'expanded';
      } else {
        next = candidates.reduce((best, c) => (
          Math.abs(c.offset - current) < Math.abs(best.offset - current) ? c : best
        )).key;
      }
      setSnap(next);
    },
    {
      axis: 'y',
      filterTaps: true,
      rubberband: true
    }
  );

  // Mount map once
  useEffect(() => {
    if (!mapContainer.current || !maplibre || !userCenter) return undefined;

    map.current = new maplibre.Map({
      container: mapContainer.current,
      style: 'https://api.maptiler.com/maps/5e93952d-a638-4302-8ab2-bab246582ff4/style.json?key=UHHUx3iqEIPjCVMQT4MD',
      center: userCenter,
      zoom: 4
    });

    const handleSelect = (id) => {
      if (id) onSelectZoneRef.current?.(id);
    };

    map.current.on('load', () => {
      new maplibre.Marker({ color: '#2196F3' }).setLngLat(userCenter).addTo(map.current);

      map.current.addSource('troops', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: buildFeatures(zones, selectedZoneIdRef.current) }
      });

      map.current.addLayer({
        id: 'troops-fill',
        type: 'fill',
        source: 'troops',
        paint: {
          'fill-color': [
            'case',
            ['==', ['get', 'id'], selectedZoneIdRef.current || ''],
            SELECTED_FILL,
            UNSELECTED_FILL
          ],
          'fill-opacity': 0.28
        }
      });
      map.current.addLayer({
        id: 'troops-stroke',
        type: 'line',
        source: 'troops',
        paint: {
          'line-color': [
            'case',
            ['==', ['get', 'id'], selectedZoneIdRef.current || ''],
            SELECTED_STROKE,
            UNSELECTED_STROKE
          ],
          'line-width': 2.5
        }
      });

      map.current.on('click', 'troops-fill', (e) => {
        const id = e.features?.[0]?.properties?.id;
        handleSelect(id);
      });
      map.current.on('mouseenter', 'troops-fill', () => {
        map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', 'troops-fill', () => {
        map.current.getCanvas().style.cursor = '';
      });

      // Initial markers
      zones.forEach((z) => {
        const el = document.createElement('div');
        markerStyle(el, z.id === selectedZoneIdRef.current);
        el.textContent = z.name;
        el.onclick = (evt) => {
          evt.stopPropagation();
          handleSelect(z.id);
        };
        const marker = new maplibre.Marker({ element: el })
          .setLngLat([z.centerLng, z.centerLat])
          .addTo(map.current);
        markersRef.current.push({ id: z.id, marker, el });
      });

      const focus = zones.find((z) => z.id === selectedZoneIdRef.current) || zones[0];
      fitUserAndTroops(map.current, userCenter, focus ? [focus] : [], sheetHeightRef.current + 24);
      map.current.resize();
    });

    const resize = () => {
      map.current?.resize();
      setViewportH(rootRef.current?.clientHeight || window.innerHeight || 700);
    };

    const ro = window.ResizeObserver
      ? new ResizeObserver(() => {
          resize();
          const focus = zones.find((z) => z.id === selectedZoneIdRef.current) || zones[0];
          if (map.current && userCenter) {
            fitUserAndTroops(map.current, userCenter, focus ? [focus] : [], sheetHeightRef.current + 24);
          }
        })
      : null;
    if (ro && rootRef.current) ro.observe(rootRef.current);
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      ro?.disconnect();
      markersRef.current.forEach(({ marker }) => marker.remove());
      markersRef.current = [];
      map.current?.remove();
      map.current = null;
    };
  }, [userCenter?.[0], userCenter?.[1]]);

  // Update zones / selection in place (no remount)
  useEffect(() => {
    if (!map.current) return;
    const source = map.current.getSource('troops');
    if (source) {
      source.setData({ type: 'FeatureCollection', features: buildFeatures(zones, selectedZoneId) });
    }
    if (map.current.getLayer('troops-fill')) {
      map.current.setPaintProperty('troops-fill', 'fill-color', [
        'case',
        ['==', ['get', 'id'], selectedZoneId || ''],
        SELECTED_FILL,
        UNSELECTED_FILL
      ]);
    }
    if (map.current.getLayer('troops-stroke')) {
      map.current.setPaintProperty('troops-stroke', 'line-color', [
        'case',
        ['==', ['get', 'id'], selectedZoneId || ''],
        SELECTED_STROKE,
        UNSELECTED_STROKE
      ]);
    }

    // Rebuild markers if zone set changed
    const existingIds = markersRef.current.map((m) => m.id).join(',');
    const nextIds = zones.map((z) => z.id).join(',');
    if (existingIds !== nextIds) {
      markersRef.current.forEach(({ marker }) => marker.remove());
      markersRef.current = [];
      zones.forEach((z) => {
        const el = document.createElement('div');
        markerStyle(el, z.id === selectedZoneId);
        el.textContent = z.name;
        el.onclick = (evt) => {
          evt.stopPropagation();
          onSelectZoneRef.current?.(z.id);
        };
        const marker = new maplibre.Marker({ element: el })
          .setLngLat([z.centerLng, z.centerLat])
          .addTo(map.current);
        markersRef.current.push({ id: z.id, marker, el });
      });
    } else {
      markersRef.current.forEach(({ id, el }) => {
        markerStyle(el, id === selectedZoneId);
      });
    }

    const focus = zones.find((z) => z.id === selectedZoneId) || zones[0];
    fitUserAndTroops(map.current, userCenter, focus ? [focus] : [], sheetHeight + 24);
    map.current.resize();
  }, [zones, selectedZoneId, userCenter, sheetHeight]);

  return (
    <Box
      ref={rootRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: fillParent ? '100%' : { xs: '70vh', md: '62vh' },
        minHeight: fillParent ? 0 : 360,
        overflow: 'hidden',
        borderRadius: fillParent ? 0 : 2,
        border: fillParent ? 'none' : '1px solid rgba(51,27,101,0.18)'
      }}
    >
      <Box
        ref={mapContainer}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%'
        }}
      />

      <motion.div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: snaps.expanded,
          y: springY,
          zIndex: 5,
          display: 'flex',
          flexDirection: 'column',
          background: 'rgba(255,255,255,0.97)',
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          boxShadow: '0 -8px 28px rgba(17,10,35,0.22)',
          overflow: 'hidden',
          touchAction: 'none'
        }}
      >
        <Box
          {...bind()}
          sx={{
            width: '100%',
            pt: 1.25,
            pb: 0.75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
            flexShrink: 0
          }}
        >
          <Box sx={{ width: 42, height: 4, borderRadius: 2, bgcolor: '#c5c0ce', mb: 1 }} />
          <Typography variant="subtitle2" sx={{ color: PASTEL_PURPLE_DARK, fontWeight: 800 }}>
            Closest Troops
          </Typography>
          {selectedZone && (
            <Typography variant="caption" sx={{ color: 'text.secondary', px: 2, textAlign: 'center' }}>
              Selected: {selectedZone.name}
              {selectedZone.distanceMiles != null ? ` · ${selectedZone.distanceMiles.toFixed(0)} mi away` : ''}
            </Typography>
          )}
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 9, minHeight: 0, touchAction: 'pan-y' }}>
          <List dense disablePadding>
            {ranked.map((z) => {
              const selected = z.id === selectedZoneId;
              return (
                <ListItemButton
                  key={z.id}
                  selected={selected}
                  onClick={() => onSelectZone?.(z.id)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    mx: 0.5,
                    border: selected ? `1.5px solid ${SELECTED_FILL}` : '1.5px solid transparent',
                    backgroundColor: selected ? 'rgba(46,125,50,0.14)' : 'transparent',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(46,125,50,0.18)',
                      '&:hover': { backgroundColor: 'rgba(46,125,50,0.24)' }
                    }
                  }}
                >
                  <ListItemText
                    primary={z.name}
                    secondary={
                      z.distanceMiles != null
                        ? `${z.distanceMiles.toFixed(0)} mi away • ${z.radiusMiles} mi radius`
                        : `${z.radiusMiles} mi radius`
                    }
                    primaryTypographyProps={{
                      fontWeight: selected ? 800 : 600,
                      color: selected ? SELECTED_STROKE : PASTEL_PURPLE_DARK
                    }}
                  />
                </ListItemButton>
              );
            })}
            {ranked.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                No troop zones yet. Ask an admin to create one.
              </Typography>
            )}
          </List>
        </Box>

      </motion.div>

      {/* Actions stay viewport-pinned at every sheet snap point. */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 6,
          p: 1.5,
          pt: 1,
          pb: 'max(12px, env(safe-area-inset-bottom))',
          display: 'flex',
          gap: 1,
          borderTop: '1px solid rgba(51,27,101,0.1)',
          background: 'rgba(255,255,255,0.98)',
          boxShadow: '0 -3px 12px rgba(17,10,35,0.08)'
        }}
      >
        {onBack && (
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={busy}
            sx={{
              flex: '0 0 auto',
              textTransform: 'none',
              fontWeight: 700,
              color: PASTEL_PURPLE_DARK,
              borderColor: 'rgba(89,69,140,0.45)',
              backgroundColor: 'rgba(89,69,140,0.06)',
              '&:hover': {
                borderColor: PASTEL_PURPLE,
                backgroundColor: 'rgba(89,69,140,0.12)'
              }
            }}
          >
            Back
          </Button>
        )}
        <Button
          fullWidth
          variant="contained"
          disabled={!selectedZoneId || busy}
          onClick={onConfirm}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            backgroundColor: PASTEL_PURPLE,
            boxShadow: '0 4px 12px rgba(89,69,140,0.28)',
            '&:hover': { backgroundColor: PASTEL_PURPLE_DARK },
            '&.Mui-disabled': {
              backgroundColor: 'rgba(89,69,140,0.28)',
              color: 'rgba(255,255,255,0.75)'
            }
          }}
        >
          {busy ? 'Saving...' : confirmLabel}
        </Button>
      </Box>
    </Box>
  );
}
